/** Maximum number of retry attempts for transient network errors. */
export const MAX_RETRIES = 2

/** Backoff delays (ms) for each retry attempt. */
export const RETRY_DELAYS = [1000, 2000] as const

/**
 * Returns true for transient network errors that are safe to retry.
 * Does NOT match server errors (4xx/5xx) — those may have been processed.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return (
    error.message.includes('Failed to fetch') ||
    error.message.includes('NetworkError') ||
    error.message.includes('net::ERR_') ||
    error.message.includes('Load failed') ||
    error.message.includes('The network connection was lost')
  )
}

export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const onAbort = () => {
      clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Selects the appropriate user-facing error message based on the error type.
 * Network errors get a connectivity-specific message; API errors pass through
 * their already-formatted message; unknown errors get the default.
 */
export function getStreamErrorDisplayMessage(
  error: unknown,
  defaultMessage: string,
  networkMessage: string,
): string {
  if (!(error instanceof Error)) {
    return defaultMessage
  }
  if (isNetworkError(error)) {
    return networkMessage
  }
  // API errors are already formatted by extractApiErrorMessage in the API layer
  return error.message || defaultMessage
}
